import { ISubmissionApiClient, SubmissionFilter } from '@cyber-range/cyber-range-api-ctf-submission-client';
import { defineStore } from 'pinia';
import { watch } from 'vue';
import { useApiClientStore } from './apiClientStore';
import { useCompetitionStore } from './competitionStore';
import { useTeamStore } from './teamStore';

// Stored outside Pinia state to preserve promise reference identity across reactive proxification.
let _initialFetch: Promise<void> | undefined;

export const useScoreStore = defineStore('scoreStore', 
{
    state: () =>
    ({
        myScore: 0,
        myScoreByCategory: {} as Record<string, number>,
        attemptsByChallenge: {} as Record<string, number>,
        _watching: false
    }),

    getters:
    {
        getScoreForCategory: (state) => (category: string): number =>
        {
            return state.myScoreByCategory[category] || 0;
        },
        getMyChallengeAttempts: (state) => (challengeId: string): number =>
        {
            return state.attemptsByChallenge[challengeId] || 0;
        }
    },

    actions: 
    {
        autoFetch(): Promise<void>
        {
            if (this._watching) return _initialFetch ?? Promise.resolve();
            this._watching = true;

            _initialFetch = useTeamStore().myTeamId
                ? this.fetchMyTeamData()
                : Promise.resolve();

            watch(
                () => useTeamStore().myTeamId,
                async (teamId) =>
                {
                    if (teamId)
                    {
                        await this.fetchMyTeamData();
                    }
                    else
                    {
                        this.$reset();
                    }
                }
            );

            return _initialFetch;
        },

        // Loads everything that depends on the team being known: the score and the per-challenge attempt counts.
        async fetchMyTeamData(): Promise<void>
        {
            await Promise.all([
                this.fetchMyScore({ background: true }),
                this.fetchMyTeamChallengeStatistics({ background: true })
            ]);
        },

        async fetchMyScore(options?: { background: boolean }): Promise<number>
        {
            const teamId = useTeamStore().myTeamId;
            if (!teamId) return 0;

            let client: ISubmissionApiClient = options?.background
                ? useApiClientStore().backgroundSubmissionApiClient
                : useApiClientStore().submissionApiClient;

            const competitionId = useCompetitionStore().currentCompetition.id;

            const filter = new SubmissionFilter({ limit: 1 });
            const page = await client.getCompetitionTeamPlayerStatistics(competitionId, teamId, filter);
            const stats = page.items[0];

            this.myScore = stats.score;
            this.myScoreByCategory = stats.scoreByCategory || {};
            return this.myScore;
        },

        // Populates the attempt-count map for every challenge the team has already attempted, in one request.
        async fetchMyTeamChallengeStatistics(options?: { background: boolean }): Promise<void>
        {
            const teamId = useTeamStore().myTeamId;
            if (!teamId) return;

            let client: ISubmissionApiClient = options?.background
                ? useApiClientStore().backgroundSubmissionApiClient
                : useApiClientStore().submissionApiClient;

            const competitionId = useCompetitionStore().currentCompetition.id;

            // This route returns every challenge the team has attempted in a single response; it does not paginate
            // and ignores any limit/token, so no filter is needed here.
            const page = await client.listCompetitionTeamChallengesStatistics(competitionId, teamId);

            const attempts: Record<string, number> = {};
            for (const stats of page.items)
            {
                attempts[stats.challengeId] = (stats.failedAttempts || 0) + (stats.successfulAttempts || 0);
            }
            this.attemptsByChallenge = attempts;
        },

        // Records a locally-made attempt so the map stays current without re-fetching from the API.
        incrementChallengeAttempt(challengeId: string): void
        {
            this.attemptsByChallenge[challengeId] = this.getMyChallengeAttempts(challengeId) + 1;
        }
    }
});
